import AngleDoubleRight from 'assets/icons/AngleDoubleRight';
import FocusBearLogoAnimated from 'assets/icons/FocusBearLogoAnimated';
import focusBearLogo from 'assets/images/focus-bear-logo.png';
import BaseButton from 'components/ui/BaseButton';
import COLOR from 'constants/color';
import { ATTRIB } from 'constants/test';
import { useWindowSize } from 'hooks/useWindowSize';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelectorShallow } from 'store/hooks';
import { navBarSelector } from 'store/reducer/setting/selectors';
import { getAccountDropDownMenus } from 'utils/navBar';
import SideNav from './SideNav';
import SideNavAccount from './SideNavAccount';
import { ROUTES } from 'constants/routes';
import { useHasManagedBlockingContext } from 'hooks/managed-blocking/useHasManagedBlockingContext';

const NavBar = () => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { width } = useWindowSize();
  const isSmallDevice = width < 1024;
  const { adminForTeams, userTeams, teacherClasses, areTeamsLoading } =
    useAppSelectorShallow(navBarSelector);
  const hasManagedBlocking = useHasManagedBlockingContext();

  useEffect(() => {
    if (isSmallDevice) {
      setIsCollapsed(true);
    }
  }, [isSmallDevice]);

  const { myCircleSubMenus, extraMenuSubMenus, profileSubMenus } = useMemo(
    () =>
      getAccountDropDownMenus(
        userTeams || [],
        adminForTeams,
        teacherClasses || [],
        areTeamsLoading,
        hasManagedBlocking
      ),
    [
      adminForTeams,
      userTeams,
      teacherClasses,
      areTeamsLoading,
      hasManagedBlocking
    ]
  );

  const toggleCollapse = useCallback(() => {
    if (isSmallDevice) {
      return;
    }
    setIsCollapsed((prev) => !prev);
  }, [isSmallDevice]);

  return (
    <aside
      className={`flex flex-col items-center lg:items-start h-full bg-primary-50 dark:bg-secondary-dark-50 border-r border-gray-200 dark:border-secondary-dark-300 transition-all duration-300 ease-in-out ${
        isCollapsed ? '~w-[4rem]/[5rem]' : '~w-56/64'
      }`}
      data-testid='side-navigation'>
      <div
        className={`flex items-center h-16 px-4 border-b border-gray-200 dark:border-secondary-dark-300 w-full ${
          isCollapsed ? 'justify-center' : 'justify-between'
        }`}>
        {!isCollapsed && (
          <Link
            to='/'
            className='flex items-center'
            data-test={ATTRIB.TEST.HOME}
            data-testid='side-nav-logo-link'>
            <img
              src={focusBearLogo}
              alt='Focus Bear'
              className='w-32 h-auto object-contain dark:brightness-0 dark:invert'
            />
          </Link>
        )}
        {isSmallDevice ? (
          <button
            onClick={() => navigate(ROUTES.HOME)}
            className='cursor-pointer'>
            <FocusBearLogoAnimated className='~w-6/8 ~h-6/8' />
          </button>
        ) : (
          <BaseButton
            icon={
              <AngleDoubleRight
                fill={COLOR.GRAY['900']}
                styles={`~w-4/6 h-auto transition-transform duration-300 dark:fill-white ${isCollapsed ? 'rotate-0' : 'rotate-180'}`}
              />
            }
            onClick={toggleCollapse}
            data-testid='side-nav-collapse-btn'
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            variant='icon'
          />
        )}
      </div>
      <SideNav
        myCircleSubMenus={myCircleSubMenus}
        extraMenuSubMenus={extraMenuSubMenus}
        isCollapsed={isCollapsed}
      />
      <SideNavAccount
        profileSubMenus={profileSubMenus}
        isCollapsed={isCollapsed}
      />
    </aside>
  );
};

export default NavBar;
